import React from 'react';
import { App } from '@capacitor/app';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { base44 } from '@/api/base44Client';
import { useAuth } from '@/lib/AuthContext';
import { clearAppIconBadge } from '@/lib/appIconBadge';
import { initializeNativePushNotifications } from '@/lib/pushNotifications';

export default function PushNotificationManager() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const initializedForUserRef = React.useRef('');

  const { data: member } = useQuery({
    queryKey: ['pushNotificationMember', user?.email],
    queryFn: async () => {
      if (!user?.email) return null;
      const members = await base44.entities.Member.filter({ email: user.email }, '-created_date', 1);
      return members?.[0] || null;
    },
    enabled: Boolean(user?.email && isAuthenticated),
  });

  React.useEffect(() => {
    if (!isAuthenticated || !user?.id) return;
    if (initializedForUserRef.current === user.id) return;

    let isCancelled = false;
    initializedForUserRef.current = user.id;

    initializeNativePushNotifications({ user, member, navigate }).catch((error) => {
      if (isCancelled) return;
      initializedForUserRef.current = '';
      console.error('Falha ao inicializar push notifications:', error);
    });

    return () => {
      isCancelled = true;
    };
  }, [isAuthenticated, member, navigate, user]);

  React.useEffect(() => {
    if (!isAuthenticated || !user?.id) return;
    clearAppIconBadge().catch((error) => {
      console.warn('Falha ao limpar badge do app:', error);
    });
  }, [isAuthenticated, user]);

  React.useEffect(() => {
    if (!isAuthenticated || !user?.id) return undefined;

    let listenerHandle;
    App.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) return;
      clearAppIconBadge().catch((error) => {
        console.warn('Falha ao limpar badge ao abrir app:', error);
      });
    }).then((handle) => {
      listenerHandle = handle;
    });

    return () => {
      listenerHandle?.remove?.();
    };
  }, [isAuthenticated, member, user]);

  React.useEffect(() => {
    if (isAuthenticated) return;
    initializedForUserRef.current = '';
    clearAppIconBadge().catch(() => {});
  }, [isAuthenticated]);

  return null;
}
